/**
 * New merchant page — full registration (legal entity) only.
 * International: fields from GET /api/private/admin/runtime merchantFields (no INN/BIC).
 * Chargex (no manifest): existing RU set. Address: Google Places or DaData via AddressSuggestField.
 */
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useNotifications from '../mui-crud/hooks/useNotifications/useNotifications';
import PageContainer from '../mui-crud/components/PageContainer';
import AddressSuggestField from '../components/AddressSuggestField';
import * as api from '../../modules/api';
import { loadAdminRuntime } from '../utils/adminRuntime';
import { getCurrentEmail, getCurrentRole, isSuperAdmin } from '../utils/accessControl';
import {
  isInternationalRuntime,
  merchantFieldsFor,
  showMerchantField,
} from '../utils/merchantFields';

const initialLegal = {
  name: '',
  surname: '',
  middleName: '',
  phoneNumber: '',
  email: '',
  password: '',
  passwordConfirm: '',
  accountNumber: '',
  bankName: '',
  bic: '',
  inn: '',
  edrpou: '',
  iban: '',
  npwp: '',
  nameOfLegalEntity: '',
  legalAddress: '',
  position: '',
  contractId: '',
  contractDate: null,
  revShare: '',
  withNDS: false,
  withoutRevShare: false,
  partnerEmail: '',
  countryId: '',
};

const MERCHANT_CREATE_COUNTRY_KEY = 'admin.merchantCreate.lastCountryId';

function readLastCountryId() {
  try {
    return localStorage.getItem(MERCHANT_CREATE_COUNTRY_KEY) || '';
  } catch {
    return '';
  }
}

function writeLastCountryId(countryId) {
  if (!countryId) {
    return;
  }
  try {
    localStorage.setItem(MERCHANT_CREATE_COUNTRY_KEY, countryId);
  } catch {
    // private mode / quota — ignore
  }
}

function pickDefaultCountryId(countries) {
  if (!countries?.length) {
    return '';
  }
  const saved = readLastCountryId();
  if (saved && countries.some((country) => country.id === saved)) {
    return saved;
  }
  return countries[0].id;
}

const FIELD_VALUE_KEY = {
  legalName: 'nameOfLegalEntity',
  address: 'legalAddress',
  givenName: 'name',
  familyName: 'surname',
  middleName: 'middleName',
  email: 'email',
  phone: 'phoneNumber',
  inn: 'inn',
  bankName: 'bankName',
  bic: 'bic',
  accountNumber: 'accountNumber',
  edrpou: 'edrpou',
  iban: 'iban',
  npwp: 'npwp',
};

const FIELD_ERROR_KEY = {
  legalName: 'nameOfLegalEntityRequired',
  address: 'legalAddressRequired',
  givenName: 'nameRequired',
  familyName: 'surnameRequired',
  email: 'emailRequired',
  phone: 'phoneRequired',
  inn: 'innRequired',
  bankName: 'bankNameRequired',
  bic: 'bicRequired',
  accountNumber: 'accountNumberRequired',
  edrpou: 'edrpouRequired',
  iban: 'ibanRequired',
  npwp: 'npwpRequired',
};

function withPhonePrefix(phone, prefix) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!prefix) {
    return digits;
  }
  if (digits.startsWith(prefix)) {
    return digits;
  }
  return prefix + digits.replace(/^0+/, '');
}

function validateLegal(values, t, merchantFields) {
  const errors = {};
  const s = (v) => (v != null && v !== '' ? String(v).trim() : '');
  if (!s(values.email)) errors.email = t('merchantCreate.emailRequired');
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (s(values.email) && !emailRe.test(values.email)) errors.email = t('merchantCreate.emailInvalid');
  if (!s(values.password)) errors.password = t('merchantCreate.passwordRequired');
  if (s(values.password) && values.password.length < 6) errors.password = t('merchantCreate.passwordMinLength');
  if (values.password !== values.passwordConfirm) errors.passwordConfirm = t('merchantCreate.passwordMismatch');

  const fields = merchantFields == null
    ? ['givenName', 'familyName', 'phone', 'accountNumber', 'bankName', 'bic', 'inn', 'legalName', 'address']
    : merchantFields;
  fields.forEach((field) => {
    const key = FIELD_VALUE_KEY[field];
    if (!key) {
      return;
    }
    if (!s(values[key])) {
      const errKey = FIELD_ERROR_KEY[field];
      errors[key] = errKey ? t(`merchantCreate.${errKey}`) : t('merchantCreate.required');
    }
  });
  return errors;
}

export default function MerchantCreate() {
  const history = useHistory();
  const { t } = useTranslation();
  const notifications = useNotifications();
  const role = getCurrentRole();
  const selfEmail = getCurrentEmail();

  const [legalValues, setLegalValues] = React.useState(() => ({
    ...initialLegal,
    partnerEmail: isSuperAdmin(role) ? '' : (selfEmail || ''),
  }));
  const [legalErrors, setLegalErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [adminsList, setAdminsList] = React.useState([]);
  const [runtime, setRuntime] = React.useState(null);

  const countries = runtime?.countries || [];
  const international = isInternationalRuntime(runtime);
  const selectedCountry = countries.find((c) => c.id === legalValues.countryId) || countries[0];
  const merchantFields = merchantFieldsFor(runtime, {
    ...legalValues,
    countryId: legalValues.countryId || selectedCountry?.id,
  });

  React.useEffect(() => {
    loadAdminRuntime().then((data) => {
      setRuntime(data);
      const list = data?.countries || [];
      if (list.length) {
        setLegalValues((prev) => ({
          ...prev,
          countryId: prev.countryId || pickDefaultCountryId(list),
        }));
      }
    });
  }, []);

  React.useEffect(() => {
    if (!isSuperAdmin(role)) {
      setAdminsList([]);
      return;
    }
    api.getAdmins().then((res) => {
      const data = res?.data;
      setAdminsList(Array.isArray(data) ? data : []);
    }).catch(() => setAdminsList([]));
  }, [role]);

  const onLegalChange = React.useCallback((name, value) => {
    if (name === 'countryId') {
      writeLastCountryId(value);
    }
    setLegalValues((prev) => ({ ...prev, [name]: value }));
    setLegalErrors((prev) => ({ ...prev, [name]: null }));
  }, []);

  const handleSubmitLegal = React.useCallback(
    async (e) => {
      e?.preventDefault();
      const errs = validateLegal(legalValues, t, merchantFields);
      if (Object.keys(errs).some((k) => errs[k])) {
        setLegalErrors(errs);
        return;
      }
      setLegalErrors({});
      setSubmitting(true);
      try {
        const contractDateFormatted = legalValues.contractDate
          ? (dayjs.isDayjs(legalValues.contractDate) ? legalValues.contractDate : dayjs(legalValues.contractDate)).format('YYYY-MM-DD')
          : undefined;
        const phone = withPhonePrefix(legalValues.phoneNumber, selectedCountry?.phonePrefix);
        await api.requestRegMerchantLegalEntity({
          name: legalValues.name.trim(),
          surname: legalValues.surname.trim(),
          middleName: (legalValues.middleName || '').trim(),
          phoneNumber: phone,
          email: legalValues.email.trim(),
          password: legalValues.password,
          accountNumber: (legalValues.accountNumber || '').trim(),
          bankName: (legalValues.bankName || '').trim(),
          bic: (legalValues.bic || '').trim(),
          inn: (legalValues.inn || '').trim(),
          edrpou: (legalValues.edrpou || '').trim(),
          iban: (legalValues.iban || '').trim(),
          npwp: (legalValues.npwp || '').trim(),
          nameOfLegalEntity: (legalValues.nameOfLegalEntity || '').trim(),
          legalAddress: (legalValues.legalAddress || '').trim(),
          position: (legalValues.position || '').trim(),
          contractId: (legalValues.contractId || '').trim() || undefined,
          contractDate: contractDateFormatted,
          revShare: legalValues.withoutRevShare ? undefined : (legalValues.revShare != null && legalValues.revShare !== '' ? Number(legalValues.revShare) : undefined),
          withNDS: international ? false : (legalValues.withNDS === 'true' || legalValues.withNDS === true),
          withoutRevShare: Boolean(legalValues.withoutRevShare),
          partnerEmail: (legalValues.partnerEmail || '').trim() || undefined,
        });
        writeLastCountryId(legalValues.countryId || selectedCountry?.id);
        notifications.show(t('merchantCreate.success'), { severity: 'success', autoHideDuration: 3000 });
        history.push('/merchants');
      } catch (err) {
        notifications.show(err?.response?.data?.message || err?.message || t('merchantCreate.error'), { severity: 'error', autoHideDuration: 5000 });
      } finally {
        setSubmitting(false);
      }
    },
    [legalValues, history, notifications, t, international, merchantFields, selectedCountry]
  );

  const handleBack = React.useCallback(() => {
    history.push('/merchants');
  }, [history]);

  const withoutConcession = Boolean(legalValues.withoutRevShare);
  const show = (field) => showMerchantField(merchantFields, field);

  return (
    <PageContainer
      title={t('merchantCreate.title')}
      breadcrumbs={[{ title: t('merchants.title'), path: '/merchants' }, { title: t('merchantCreate.new') }]}
    >
      <Box sx={{ maxWidth: 640 }}>
        <Box component="form" onSubmit={handleSubmitLegal} noValidate>
          <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">{t('drawer.merchant.details')}</Typography>
            {international && (
              <FormControl fullWidth size="small">
                <InputLabel>{t('merchantCreate.country')}</InputLabel>
                <Select
                  value={legalValues.countryId || selectedCountry?.id || ''}
                  label={t('merchantCreate.country')}
                  onChange={(e) => onLegalChange('countryId', e.target.value)}
                >
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id}>{country.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField fullWidth label={t('drawer.merchant.email')} type="email" value={legalValues.email} onChange={(e) => onLegalChange('email', e.target.value)} error={!!legalErrors.email} helperText={legalErrors.email ?? ' '} />
            <TextField fullWidth label={t('merchantCreate.password')} type="password" value={legalValues.password} onChange={(e) => onLegalChange('password', e.target.value)} error={!!legalErrors.password} helperText={legalErrors.password ?? ' '} />
            <TextField fullWidth label={t('merchantCreate.passwordConfirm')} type="password" value={legalValues.passwordConfirm} onChange={(e) => onLegalChange('passwordConfirm', e.target.value)} error={!!legalErrors.passwordConfirm} helperText={legalErrors.passwordConfirm ?? ' '} />

            <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>{t('drawer.merchant.company')}</Typography>
            {show('legalName') && (
              <TextField fullWidth label={t('drawer.merchant.nameOfLegalEntity')} value={legalValues.nameOfLegalEntity ?? ''} onChange={(e) => onLegalChange('nameOfLegalEntity', e.target.value)} error={!!legalErrors.nameOfLegalEntity} helperText={legalErrors.nameOfLegalEntity ?? ' '} />
            )}
            {show('inn') && (
              <TextField fullWidth label={t('drawer.merchant.inn')} value={legalValues.inn ?? ''} onChange={(e) => onLegalChange('inn', e.target.value)} error={!!legalErrors.inn} helperText={legalErrors.inn ?? ' '} />
            )}
            {show('edrpou') && (
              <TextField fullWidth label={t('drawer.merchant.edrpou')} value={legalValues.edrpou ?? ''} onChange={(e) => onLegalChange('edrpou', e.target.value)} error={!!legalErrors.edrpou} helperText={legalErrors.edrpou ?? ' '} />
            )}
            {show('npwp') && (
              <TextField fullWidth label={t('drawer.merchant.npwp')} value={legalValues.npwp ?? ''} onChange={(e) => onLegalChange('npwp', e.target.value)} error={!!legalErrors.npwp} helperText={legalErrors.npwp ?? ' '} />
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {show('familyName') && (
                <TextField fullWidth label={t('drawer.merchant.surname')} value={legalValues.surname ?? ''} onChange={(e) => onLegalChange('surname', e.target.value)} error={!!legalErrors.surname} helperText={legalErrors.surname ?? ' '} />
              )}
              {show('givenName') && (
                <TextField fullWidth label={t('drawer.merchant.name')} value={legalValues.name ?? ''} onChange={(e) => onLegalChange('name', e.target.value)} error={!!legalErrors.name} helperText={legalErrors.name ?? ' '} />
              )}
              {show('middleName') && (
                <TextField fullWidth label={t('drawer.merchant.middleName')} value={legalValues.middleName ?? ''} onChange={(e) => onLegalChange('middleName', e.target.value)} />
              )}
            </Stack>
            <TextField fullWidth label={t('drawer.merchant.position')} value={legalValues.position ?? ''} onChange={(e) => onLegalChange('position', e.target.value)} />
            {show('phone') && (
              <TextField
                fullWidth
                label={t('drawer.merchant.phoneNumber')}
                value={legalValues.phoneNumber ?? ''}
                onChange={(e) => onLegalChange('phoneNumber', e.target.value)}
                error={!!legalErrors.phoneNumber}
                helperText={legalErrors.phoneNumber ?? (selectedCountry?.phonePrefix ? `+${selectedCountry.phonePrefix}` : ' ')}
              />
            )}
            {show('address') && (
              <AddressSuggestField
                label={t('drawer.merchant.legalAddress')}
                value={legalValues.legalAddress ?? ''}
                onChange={(v) => onLegalChange('legalAddress', v)}
                onSelect={(suggestion) => onLegalChange('legalAddress', suggestion?.address || '')}
                error={!!legalErrors.legalAddress}
                helperText={legalErrors.legalAddress ?? ' '}
              />
            )}

            {(show('bankName') || show('accountNumber') || show('bic') || show('iban')) && (
              <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>{t('drawer.merchant.bank')}</Typography>
            )}
            {show('bankName') && (
              <TextField fullWidth label={t('drawer.merchant.bankName')} value={legalValues.bankName ?? ''} onChange={(e) => onLegalChange('bankName', e.target.value)} error={!!legalErrors.bankName} helperText={legalErrors.bankName ?? ' '} />
            )}
            {show('accountNumber') && (
              <TextField fullWidth label={t('drawer.merchant.accountNumber')} value={legalValues.accountNumber ?? ''} onChange={(e) => onLegalChange('accountNumber', e.target.value)} error={!!legalErrors.accountNumber} helperText={legalErrors.accountNumber ?? ' '} />
            )}
            {show('bic') && (
              <TextField fullWidth label={t('drawer.merchant.bic')} value={legalValues.bic ?? ''} onChange={(e) => onLegalChange('bic', e.target.value)} error={!!legalErrors.bic} helperText={legalErrors.bic ?? ' '} />
            )}
            {show('iban') && (
              <TextField fullWidth label={t('drawer.merchant.iban')} value={legalValues.iban ?? ''} onChange={(e) => onLegalChange('iban', e.target.value)} error={!!legalErrors.iban} helperText={legalErrors.iban ?? ' '} />
            )}

            <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>{t('drawer.merchant.contract')}</Typography>
            <TextField fullWidth label={t('drawer.merchant.contractNumber')} value={legalValues.contractId} onChange={(e) => onLegalChange('contractId', e.target.value)} />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t('drawer.merchant.contractDate')}
                value={legalValues.contractDate ? (dayjs.isDayjs(legalValues.contractDate) ? legalValues.contractDate : dayjs(legalValues.contractDate)) : null}
                onChange={(d) => onLegalChange('contractDate', d)}
                format="DD.MM.YYYY"
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </LocalizationProvider>
            <FormControlLabel
              control={<Checkbox checked={legalValues.withoutRevShare} onChange={(e) => onLegalChange('withoutRevShare', e.target.checked)} />}
              label={t('merchantCreate.withoutConcession')}
            />
            <TextField
              fullWidth
              label={t('drawer.merchant.concessionAmount')}
              type="number"
              value={legalValues.revShare}
              onChange={(e) => onLegalChange('revShare', e.target.value)}
              disabled={withoutConcession}
            />
            {show('withNDS') && (
              <FormControl fullWidth size="small">
                <InputLabel>{t('drawer.merchant.withNDS')}</InputLabel>
                <Select value={legalValues.withNDS === true || legalValues.withNDS === 'true' ? 'true' : 'false'} label={t('drawer.merchant.withNDS')} onChange={(e) => onLegalChange('withNDS', e.target.value === 'true')}>
                  <MenuItem value="false">{t('common.no')}</MenuItem>
                  <MenuItem value="true">{t('common.yes')}</MenuItem>
                </Select>
              </FormControl>
            )}

            <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>{t('drawer.merchant.partner')}</Typography>
            {isSuperAdmin(role) ? (
            <FormControl fullWidth size="small">
              <InputLabel>{t('drawer.merchant.partner')}</InputLabel>
              <Select
                value={legalValues.partnerEmail || ''}
                label={t('drawer.merchant.partner')}
                onChange={(e) => onLegalChange('partnerEmail', e.target.value)}
              >
                <MenuItem value="">—</MenuItem>
                {adminsList.map((admin) => (
                  <MenuItem key={admin.email || admin.id} value={admin.email || ''}>{admin.email}</MenuItem>
                ))}
              </Select>
            </FormControl>
            ) : (
            <TextField
              fullWidth
              size="small"
              label={t('drawer.merchant.partner')}
              value={legalValues.partnerEmail || selfEmail || ''}
              InputProps={{ readOnly: true }}
            />
            )}

            <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 3 }}>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
                {t('stationPage.back')}
              </Button>
              <Button type="submit" variant="contained" size="large" disabled={submitting}>
                {submitting ? t('drawer.saving') : t('merchantCreate.create')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </PageContainer>
  );
}
